/**
 * Onboarding ("Mexican Pizza") — scripted first-session state.
 * Pure data + transitions only (no window/document/PIXI).
 *
 * Goal: a new player must not mistake this for a plain coin clicker. Instead of
 * explaining, we make them *feel* the twist:
 *   tap (0) -> nudge to the exchange (1) -> buy the target coin (2)
 *   -> scripted pump (3) -> take profit / done (4).
 *
 * The scripted pump only touches the target coin while step === 3. Normal
 * players (done === true) are never affected — their market is untouched.
 */

export interface OnboardingState {
  /** 0..4 scripted step (4 = finished celebration / done) */
  step: number;
  /** true once finished or skipped (a normal player from here on) */
  done: boolean;
  /** target meme coin for the scripted first buy + pump */
  coinId: string;
  /** timestamp the scripted pump started (0 = not pumping) */
  pumpStartAt: number;
  /** price captured at the onboarding buy (pump ramps from here) */
  pumpBasePrice: number;
  /** true once the one-time starter funds were granted */
  grantedStarter: boolean;
}

/** taps required before nudging the player toward the exchange */
export const ONBOARDING_TAP_GOAL = 5;
/** meme coin the scripted flow buys + pumps (must start listed) */
export const ONBOARDING_COIN_ID = 'chickencoin';
/** one-time starter cash floor granted on first exchange entry (small, once) */
export const ONBOARDING_STARTER_COINS = 300;
/** scripted pump window in ms */
export const ONBOARDING_PUMP_DURATION = 16_000;
/** scripted pump total gain (+120%, inside the 80-150% design band) */
export const ONBOARDING_PUMP_GAIN = 1.2;
/** terminal step value used for a finished / skipped onboarding */
export const ONBOARDING_DONE_STEP = 4;

export function createInitialOnboarding(): OnboardingState {
  return {
    step: 0,
    done: false,
    coinId: ONBOARDING_COIN_ID,
    pumpStartAt: 0,
    pumpBasePrice: 0,
    grantedStarter: false,
  };
}

/** A finished onboarding (existing players on load, or a skip). */
export function completedOnboarding(): OnboardingState {
  return {
    step: ONBOARDING_DONE_STEP,
    done: true,
    coinId: ONBOARDING_COIN_ID,
    pumpStartAt: 0,
    pumpBasePrice: 0,
    grantedStarter: true,
  };
}

/**
 * Migrate a loaded save (backward compatible).
 * Saves that predate onboarding have no `onboarding` field -> that player
 * already knows the game, so the flow is skipped (done). A save captured
 * mid-onboarding keeps its progress so a reload resumes where it left off.
 */
export function migrateOnboarding(saved: unknown): OnboardingState {
  if (saved && typeof saved === 'object' && typeof (saved as OnboardingState).step === 'number') {
    const s = saved as Partial<OnboardingState>;
    return {
      step: s.step ?? 0,
      done: !!s.done,
      coinId: s.coinId || ONBOARDING_COIN_ID,
      pumpStartAt: s.pumpStartAt ?? 0,
      pumpBasePrice: s.pumpBasePrice ?? 0,
      grantedStarter: !!s.grantedStarter,
    };
  }
  return completedOnboarding();
}

/** step 0 -> 1 once the tap goal is reached. */
export function onboardingAfterTaps(ob: OnboardingState, totalClicks: number): OnboardingState {
  if (ob.done || ob.step !== 0) return ob;
  return totalClicks >= ONBOARDING_TAP_GOAL ? { ...ob, step: 1 } : ob;
}

/** step 1 -> 2 when the player opens the exchange (starter grant is the caller's job). */
export function onboardingAfterOpenExchange(ob: OnboardingState): OnboardingState {
  if (ob.done || ob.step !== 1) return ob;
  return { ...ob, step: 2, grantedStarter: true };
}

/** step 2 -> 3 when the target coin is bought; captures the pump base price. */
export function onboardingAfterBuy(
  ob: OnboardingState,
  coinId: string,
  price: number,
  now: number
): OnboardingState {
  if (ob.done || ob.step !== 2 || coinId !== ob.coinId) return ob;
  return { ...ob, step: 3, pumpStartAt: now, pumpBasePrice: price };
}

/** step 3 -> 4 (done) when the target coin is sold; the pump releases. */
export function onboardingAfterSell(ob: OnboardingState, coinId: string): OnboardingState {
  if (ob.done || ob.step !== 3 || coinId !== ob.coinId) return ob;
  return { ...ob, step: ONBOARDING_DONE_STEP, done: true, pumpStartAt: 0, pumpBasePrice: 0 };
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

/**
 * Scripted pump price override for the onboarding target coin during step 3.
 * Ramps monotonically (ease-out) from pumpBasePrice up to +ONBOARDING_PUMP_GAIN
 * over the pump window, then holds at the peak until the player sells. Returns
 * null for every non-onboarding case, so finished players are never touched.
 */
export function onboardingPumpPrice(
  ob: OnboardingState | undefined,
  coinId: string,
  currentPrice: number,
  now: number
): number | null {
  if (!ob || ob.done || ob.step !== 3 || coinId !== ob.coinId) return null;
  if (ob.pumpStartAt <= 0 || ob.pumpBasePrice <= 0) return null;
  const progress = clamp((now - ob.pumpStartAt) / ONBOARDING_PUMP_DURATION, 0, 1);
  const eased = 1 - (1 - progress) * (1 - progress);
  const target = ob.pumpBasePrice * (1 + ONBOARDING_PUMP_GAIN * eased);
  const noise = 1 + Math.random() * 0.008;
  // Monotonic: never dips below where it already climbed (guaranteed take-profit).
  return Math.max(currentPrice, target * noise);
}
